/**
 * PlanTask TipTap Node Extension
 * 
 * Custom node for rendering plan task blocks within the document editor.
 * Plan tasks display human-readable task information while storing
 * machine-readable operation data for execution.
 */

import { Node, mergeAttributes } from '@tiptap/core';
import { ReactNodeViewRenderer } from '@tiptap/react';
import { PlanTaskComponent } from '../PlanTaskComponent';

export interface PlanTaskNodeOptions {
  HTMLAttributes: Record<string, unknown>;
}

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    planTask: {
      /**
       * Insert a plan task block
       */
      insertPlanTask: (options: {
        description: string;
        status?: 'pending' | 'in_progress' | 'completed' | 'skipped' | 'failed';
        order?: number;
        targetDocumentName?: string;
        targetDocumentId?: string;
      }) => ReturnType;
    };
  }
}

export const PlanTaskNode = Node.create<PlanTaskNodeOptions>({
  name: 'planTask',
  group: 'block',
  atom: true, // Non-editable as a whole unit
  draggable: true,

  addOptions() {
    return {
      HTMLAttributes: {},
    };
  },

  addAttributes() {
    return {
      'data-block-id': {
        default: null,
        parseHTML: element => element.getAttribute('data-block-id'),
        renderHTML: attributes => {
          if (!attributes['data-block-id']) return {};
          return { 'data-block-id': attributes['data-block-id'] };
        },
      },
      'data-internal-id': {
        default: null,
        parseHTML: element => element.getAttribute('data-internal-id'),
        renderHTML: attributes => {
          if (!attributes['data-internal-id']) return {};
          return { 'data-internal-id': attributes['data-internal-id'] };
        },
      },
      description: {
        default: '',
        parseHTML: element => element.getAttribute('data-description'),
        renderHTML: attributes => ({ 'data-description': attributes.description }),
      },
      status: {
        default: 'pending',
        parseHTML: element => element.getAttribute('data-status'),
        renderHTML: attributes => ({ 'data-status': attributes.status }),
      },
      order: {
        default: 0,
        parseHTML: element => {
          const val = element.getAttribute('data-order');
          return val ? parseInt(val, 10) : 0;
        },
        renderHTML: attributes => ({ 'data-order': String(attributes.order) }),
      },
      targetDocumentName: {
        default: null,
        parseHTML: element => element.getAttribute('data-target-document-name'),
        renderHTML: attributes => {
          if (!attributes.targetDocumentName) return {};
          return { 'data-target-document-name': attributes.targetDocumentName };
        },
      },
      targetDocumentId: {
        default: null,
        parseHTML: element => element.getAttribute('data-target-document-id'),
        renderHTML: attributes => {
          if (!attributes.targetDocumentId) return {};
          return { 'data-target-document-id': attributes.targetDocumentId };
        },
      },
      result: {
        default: null,
        parseHTML: element => element.getAttribute('data-result'),
        renderHTML: attributes => {
          if (!attributes.result) return {};
          return { 'data-result': attributes.result };
        },
      },
      error: {
        default: null,
        parseHTML: element => element.getAttribute('data-error'),
        renderHTML: attributes => {
          if (!attributes.error) return {};
          return { 'data-error': attributes.error };
        },
      },
    };
  },

  parseHTML() {
    return [
      {
        tag: 'div[data-type="plan-task"]',
      },
    ];
  },

  renderHTML({ HTMLAttributes }) {
    return [
      'div',
      mergeAttributes(this.options.HTMLAttributes, HTMLAttributes, {
        'data-type': 'plan-task',
        class: 'plan-task-node',
      }),
    ];
  },

  addNodeView() {
    return ReactNodeViewRenderer(PlanTaskComponent);
  },

  addCommands() {
    return {
      insertPlanTask:
        (options) =>
        ({ commands }) => {
          const blockId = `task-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
          return commands.insertContent({
            type: this.name,
            attrs: {
              'data-block-id': blockId,
              'data-internal-id': blockId,
              description: options.description,
              status: options.status || 'pending',
              order: options.order || 0,
              targetDocumentName: options.targetDocumentName || null,
              targetDocumentId: options.targetDocumentId || null,
            },
          });
        },
    };
  },
});
